import { TeoError } from '../errors/TeoError';
import {
  type AdditionalProps,
  type AIPrompt,
  type AiRequest,
  type AiSuggestionData,
  type APITeoMarker,
  type ExcerptContext,
} from '../TeoApi';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { AnalysisConfiguration, TeoText } from './TeoText';

export type AISuggestion = {
  context: ExcerptContext;
  markerId: string;
  tpi: number;
  replacement: string;
  error?: boolean;
};

export type AIHandlerEvents = {
  aiModeChanged: (detail: AIPrompt | null) => void;
  aiSuggestionReceived: (detail: AISuggestion) => void;
  loading: (detail: boolean) => void;
};

export class AIHandler {
  public events = new TeoEventTarget<AIHandlerEvents>();
  private _currentPromptList: AIPrompt[] = [];
  private activePrompt: AIPrompt | null = null;
  // TODO@Leonard this was private, maybe it should be protected and base of BrandfitHandler?
  public activeExcerpt?: ExcerptContext | null;
  private additionalProps: AdditionalProps | undefined;
  private _activeSuggestions: AISuggestion[] = [];
  private _isLoading = false;
  private _otherMarkersAllowed = true;
  public lastSuggestionDebug: AiSuggestionData | null = null;
  private _ignoreMarkerEvents = false;
  private markerClickRemover: (() => void) | null = null;
  private promptMarkerMap: Record<
    string,
    { markers: string[]; contextMarker?: string; allowOtherMarkers: boolean }
  > = {
    suggestions_ai_context: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_ai: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_vi_context: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_hi: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_hi_context: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_ii: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_ii_context: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_any_context: {
      markers: ['paragraph'],
      allowOtherMarkers: true,
    },
    suggestions_long_words_sentence: {
      markers: ['sentence'],
      allowOtherMarkers: true,
    },
    suggestions_abstracts_sentence: {
      markers: ['sentence'],
      allowOtherMarkers: true,
    },
    suggestions_nouns_sentence: {
      markers: ['sentence'],
      allowOtherMarkers: true,
    },
    suggestions_any_sentence: {
      markers: ['sentence'],
      allowOtherMarkers: true,
    },
    suggestions_noun_focus: {
      markers: ['simple_noun', 'verb_noun'],
      contextMarker: 'sentence',
      allowOtherMarkers: false,
    },
    suggestions_abstract_focus: {
      markers: ['abstract_noun', 'verb_noun'],
      contextMarker: 'sentence',
      allowOtherMarkers: false,
    },
    suggestions_long_word_focus: {
      markers: ['long_word', 'medium_long_word'],
      contextMarker: 'sentence',
      allowOtherMarkers: false,
    },
    suggestions_substantiations_focus: {
      markers: ['verb_noun'],
      contextMarker: 'sentence',
      allowOtherMarkers: false,
    },
    suggestions_lsentence: {
      markers: ['long_sentence'],
      allowOtherMarkers: false,
    },
    suggestions_passive: {
      markers: ['passive_sentence'],
      allowOtherMarkers: false,
    },
  };
  private alwaysAllowedMarkers: string[] = ['editor_diff'];

  constructor(private text: TeoText) {
    this.text.ui.events.addEventListener('markers', (markers) => {
      if (this._ignoreMarkerEvents) return;
      // this._ignoreMarkerEvents = true;
      if (!this.checkMarkers(markers)) {
        console.warn('Markers are not compatible with the active AI prompt.');
        this.disableAiMode(false);
      } else if (this.activePrompt) {
        this.text.markers.addMarkers([], this.getPromptMarkers(this.activePrompt), true);
      }
    });
    this.text.ui.events.addEventListener('view', () => {
      this.disableAiMode();
    });
  }

  get activeSuggestions(): AISuggestion[] {
    return this._activeSuggestions;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get isEnabled(): boolean {
    return this.activePrompt !== null;
  }

  get currentPromptList(): AIPrompt[] {
    return this._currentPromptList;
  }

  set isLoading(value: boolean) {
    if (this._isLoading === value) return;
    this._isLoading = value;
    this.events.triggerEvent('loading', value);
  }

  /**
   * Checks if the provided markers are compatible with the active AI prompt.
   * @param markers The markers to check.
   * @returns True if the markers are compatible, false otherwise.
   */
  private checkMarkers(markers: string[]): boolean {
    if (!this.activePrompt || this._otherMarkersAllowed) return true;
    const allowedMarkers = this.promptMarkerMap[this.activePrompt.type]?.markers || [];
    return markers.every((marker) => {
      return allowedMarkers.includes(marker) || this.alwaysAllowedMarkers.includes(marker);
    });
  }

  updatePromptList(): AIPrompt[] {
    if (this.text.ui.state.view === 'style') {
      this._currentPromptList = this.text.style.currentInstruction?.ai_prompts || [];
    } else {
      const currentInstruction = this.text.analysis?.currentInstruction;
      if (!currentInstruction) {
        this._currentPromptList = [];
      } else {
        this._currentPromptList = [];
        this._currentPromptList.push(...currentInstruction.ai_prompts);
        this.text.analysis?.getInstructions().forEach((instruction) => {
          if (
            instruction.index_name == currentInstruction.index_name &&
            instruction.id != currentInstruction.id
          ) {
            this._currentPromptList.push(...instruction.ai_prompts);
          }
        });
      }
    }
    this._currentPromptList.forEach((prompt) => {
      if (prompt.available === undefined || prompt.available === true) {
        prompt.available = this.checkPromptAvailability(prompt);
      }
    });
    return this._currentPromptList;
  }

  checkPromptAvailability(prompt: AIPrompt): boolean {
    if (!this.text.analysis) {
      console.warn('Text analysis is not available. Cannot check prompt availability.');
      return false;
    }
    const markers = this.text.analysis.getMarkers();
    if (!this.promptMarkerMap[prompt.type]?.allowOtherMarkers) {
      const requiredMarkers = this.getPromptMarkers(prompt);
      return requiredMarkers.some((marker) => markers.some((m) => m.marker == marker));
    }
    return true;
  }

  useSuggestion(suggestion: Pick<AISuggestion, 'markerId' | 'replacement'>): void {
    this.text.markers.replaceMarkerContent(suggestion.markerId, suggestion.replacement);
  }

  /**
   * Enables AI mode for a specific AI prompt.
   * @param prompt The AI prompt to activate.
   */
  enableAiMode(prompt: AIPrompt): void {
    if (this.activePrompt) {
      this.disableAiMode();
    }
    this.text.diff.update(this.text.text, true);
    this._otherMarkersAllowed = this.promptMarkerMap[prompt.type]?.allowOtherMarkers || false;
    this._ignoreMarkerEvents = true;
    if (!this._otherMarkersAllowed) {
      this.text.markers.setMarkers([], this.getPromptMarkers(prompt));
    } else {
      this.text.markers.addMarkers([], this.getPromptMarkers(prompt), true);
    }

    setTimeout(() => {
      this._ignoreMarkerEvents = false;
    }, 100); // Allow some time for the markers to be set before enabling marker events

    this.activePrompt = prompt;

    this.markerClickRemover = this.text.markers.events.addEventListener(
      'markerClicked',
      ((detail: { markerId: string; marker: APITeoMarker }) => {
        this.handleMarkerClick(detail);
      }).bind(this),
    );
    this.events.triggerEvent('aiModeChanged', prompt);
  }

  /**
   * Disables AI mode and removes any active listeners.
   */
  disableAiMode(removeMarkers = true): void {
    if (!this.activePrompt) return;

    if (this.markerClickRemover) {
      this.markerClickRemover();
      this.markerClickRemover = null;
    }

    if (removeMarkers) {
      this.text.markers.removeMarkers(this.getPromptMarkers(this.activePrompt));
    }
    this.activePrompt = null;
    this.activeExcerpt = null;
    this._activeSuggestions = [];
    this._otherMarkersAllowed = true;
    this.text.diff.hide();
    this.text.markers.focusMarkers([]);
    this.events.triggerEvent('aiModeChanged', null);

    console.log('AI mode disabled.');
  }

  async resendActive(modifier: string): Promise<void> {
    if (!this.activePrompt) {
      console.warn('No active AI prompt to resend request.');
      return;
    }
    if (!this.activeExcerpt) {
      console.warn('No active excerpt context to resend request.');
      return;
    }
    console.log('Resending AI request...', 'Unused modifier:', modifier);
    this.isLoading = true;
    this.handleAiSuggestion(await this.getAiSuggestion());
  }

  /**
   * Handles marker clicks when AI mode is enabled.
   * Sends an AI suggestion request with the context of the clicked marker.
   * @param detail The marker click details.
   */
  private async handleMarkerClick(detail: {
    markerId: string;
    marker: APITeoMarker;
  }): Promise<void> {
    const { markerId, marker } = detail;
    this.activeExcerpt = this.text.analysis?.createMarkerContext(
      marker,
      markerId,
      this.activePrompt ? this.getPromptContextMarker(this.activePrompt) : undefined,
    );
    console.log(
      `Marker clicked: ${markerId}. Sending AI suggestion request...`,
      this.activeExcerpt,
    );
    this.text.markers.focusMarkers([markerId]);
    this._activeSuggestions = [];
    this.handleAiSuggestion(await this.getAiSuggestion());
  }

  /**
   * Constructs the AI request object based on the current context and prompt.
   * @returns The AI request object or null if not applicable.
   */
  private getAiRequest(): AiRequest {
    if (!this.activePrompt || !this.activeExcerpt) {
      throw new Error('No active AI prompt or excerpt context available.');
    }
    if (!this.text.configIsSet()) {
      throw new Error('Text configuration is not set. Cannot send AI suggestion request.');
    }
    return {
      context: this.activeExcerpt,
      additionalProps: this.additionalProps,
      tool: this.activePrompt.type,
      tool_id: this.activePrompt.id,
      config: {
        ...this.text.configuration,
        evaluation_profile: this.text.core.dynamicConfig.options.evaluationProfileId,
      } as AnalysisConfiguration & { evaluation_profile: number },
    };
  }

  /**
   * Sends a request to the API to get a suggestion based on the current context.
   * @returns The AI suggestion data.
   */
  private async getAiSuggestion(): Promise<AiSuggestionData> {
    this.isLoading = true;
    try {
      const aiRequest = this.getAiRequest();
      const suggestion = await this.text.core.api.getAiSuggestion(aiRequest);
      this.isLoading = false;
      console.log('AI suggestion received:', suggestion);
      if (TeoError.isError(suggestion)) {
        throw new Error(`API error: ${suggestion.message}`);
      }
      return suggestion;
    } catch (error) {
      console.error('Failed to get AI suggestion:', error);
      this.text.core.notifications.addError(error);
      this.isLoading = false;
      throw error;
    }
  }

  private getPromptMarkers(prompt: AIPrompt): string[] {
    const markers = this.promptMarkerMap[prompt.type]?.markers;
    if (!markers) {
      console.warn(`No markers found for prompt type: ${prompt.type}`);
      return ['paragraph'];
    }
    return markers;
  }

  private getPromptContextMarker(prompt: AIPrompt): string | undefined {
    return this.promptMarkerMap[prompt.type]?.contextMarker;
  }

  /**
   * Handles the AI suggestion response.
   * @param suggestion The AI suggestion data.
   */
  // TODO@Leonard this was private

  public handleAiSuggestion(suggestion: AiSuggestionData): void {
    this.lastSuggestionDebug = suggestion;
    const aiSuggestion = this.createAiSuggestion(suggestion);
    this._activeSuggestions.push(aiSuggestion);
    this.events.triggerEvent('aiSuggestionReceived', aiSuggestion);
  }

  private createAiSuggestion(rawSuggestion: AiSuggestionData): AISuggestion {
    if (!rawSuggestion) {
      throw new Error('Invalid AI suggestion data received.');
    }
    if (!this.activeExcerpt) {
      throw new Error('No active excerpt context available.');
    }
    if (rawSuggestion.suggestions.length === 0) {
      if (rawSuggestion.error) {
        return {
          context: this.activeExcerpt,
          markerId: this.activeExcerpt.replacementWrapper,
          tpi: 0,
          replacement: rawSuggestion.error_message,
          error: true,
        };
      } else {
        throw new Error('No suggestions found in the AI suggestion data.');
      }
    }
    const tpi =
      rawSuggestion.suggestions[0]!.teo_result.tpi.percentage -
      rawSuggestion.base_result.tpi.percentage;
    return {
      replacement: rawSuggestion.suggestions[0]!.text,
      tpi,
      markerId: this.activeExcerpt.replacementWrapper,
      context: this.activeExcerpt,
    };
  }
}
